import React, { createContext, useContext, useState, useCallback } from 'react';

const DashboardContext = createContext();

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

export const DashboardProvider = ({ children }) => {
  const [dashboardData, setDashboardData] = useState({
    user: null,
    subjects: [],
    stats: {
      subjectsCount: 0,
      filesCount: 0,
      studyTime: 0,
      progress: 0
    },
    recentActivity: [],
    lastFetched: null,
    isLoading: false,
    error: null
  });

  const updateDashboardData = useCallback((data) => {
    setDashboardData(prev => ({
      ...prev,
      ...data,
      lastFetched: new Date().getTime()
    }));
  }, []);

  const clearDashboardData = useCallback(() => {
    setDashboardData({
      user: null,
      subjects: [],
      stats: {
        subjectsCount: 0,
        filesCount: 0,
        studyTime: 0,
        progress: 0
      },
      recentActivity: [],
      lastFetched: null,
      isLoading: false,
      error: null
    });
  }, []);

  const shouldRefreshData = useCallback(() => {
    if (!dashboardData.lastFetched) return true;
    
    // Refresh if data is older than 5 minutes
    const fiveMinutes = 5 * 60 * 1000;
    const now = new Date().getTime();
    return (now - dashboardData.lastFetched) > fiveMinutes;
  }, [dashboardData.lastFetched]);

  const value = {
    ...dashboardData,
    updateDashboardData,
    clearDashboardData,
    shouldRefreshData
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};